import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import { GROUP_PLAY_SCHEDULE } from '../constants/groupPlay'
import './GroupPlay.css'

interface CompetitorAssignment {
  id: number
  username: string
  discordUsername: string | null
  discordGlobalName: string | null
  groupPlaySlot: string | null
}

interface GroupPlayGameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
  player1User?: {
    id: number
    displayName: string
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    displayName: string
    teamAbbreviation: string
  }
  forfeit?: {
    outcome: 'home_win' | 'away_win' | 'double_loss'
    resultByUserId: Record<number, 'win' | 'loss'>
  }
}

const GROUP_NAMES: Record<number, string> = {
  1: 'Prophet',
  2: 'Revelation',
  3: 'Horsemen',
  4: 'Harbinger',
  5: 'Oracle',
  6: 'Augur',
}

/**
 * Public group-play schedule with assigned competitor names substituted for slot labels.
 */
export default function GroupPlay() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<CompetitorAssignment[]>([])
  const [games, setGames] = useState<GroupPlayGameSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersResponse, gamesResponse] = await Promise.all([
          api.get<CompetitorAssignment[]>('/users'),
          api.get<GroupPlayGameSummary[]>('/games'),
        ])
        setUsers(usersResponse.data ?? [])
        setGames(gamesResponse.data ?? [])
      } catch (error) {
        console.error('Failed to load group play assignments:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  const slotToUser = useMemo(
    () =>
      new Map(
        users
          .filter(user => user.groupPlaySlot)
          .map(user => [
            user.groupPlaySlot as string,
            {
              id: user.id,
              displayName: user.discordGlobalName ?? user.discordUsername ?? user.username,
            },
          ])
      ),
    [users]
  )

  const matchupResults = useMemo(() => {
    const results = new Map<
      string,
      {
        gameId: number
        isForfeit: boolean
        scoresByUser: Record<
          number,
          {
            score: number
            teamAbbreviation: string | null
            forfeitResult?: 'win' | 'loss'
          }
        >
      }
    >()

    for (const game of games) {
      const player1Id = game.player1User?.id
      const player2Id = game.player2User?.id
      if (!player1Id || !player2Id) {
        continue
      }

      const key = [player1Id, player2Id].sort((a, b) => a - b).join('-')
      if (results.has(key)) {
        continue
      }

      results.set(key, {
        gameId: game.id,
        isForfeit: Boolean(game.forfeit),
        scoresByUser: {
          [player1Id]: {
            score: game.homeScore,
            teamAbbreviation: game.player1User?.teamAbbreviation ?? game.homeTeam ?? null,
            forfeitResult: game.forfeit?.resultByUserId[player1Id],
          },
          [player2Id]: {
            score: game.awayScore,
            teamAbbreviation: game.player2User?.teamAbbreviation ?? game.awayTeam ?? null,
            forfeitResult: game.forfeit?.resultByUserId[player2Id],
          },
        },
      })
    }

    return results
  }, [games])

  const groupedSchedule = useMemo(
    () =>
      Array.from({ length: 6 }, (_, index) => {
        const group = index + 1
        return {
          group,
          matchups: GROUP_PLAY_SCHEDULE.filter(matchup => matchup.group === group),
        }
      }),
    []
  )

  return (
    <div className="group-play-page">
      <h1>Group Play</h1>
      <p className="group-play-subtitle">
        Group assignments replace slot numbers automatically as competitors are assigned in Admin.
      </p>

      {loading && <p className="group-play-subtitle">Loading schedule assignments...</p>}

      <div className="group-play-grid">
        {groupedSchedule.map(section => (
          <section key={section.group} className="group-play-card">
            <h2>Group {section.group} - {GROUP_NAMES[section.group]}</h2>
            <div className="group-play-table-wrapper">
              <table className="group-play-table">
                <thead>
                  <tr>
                    <th>Home</th>
                    <th className="group-play-vs-column">Matchup</th>
                    <th>Away</th>
                  </tr>
                </thead>
                <tbody>
                  {section.matchups.map(matchup => {
                    const homeUser = slotToUser.get(matchup.home)
                    const awayUser = slotToUser.get(matchup.away)
                    const resultKey =
                      homeUser && awayUser
                        ? [homeUser.id, awayUser.id].sort((a, b) => a - b).join('-')
                        : null
                    const matchupRecord = resultKey ? matchupResults.get(resultKey) : null
                    const homeResult = homeUser ? matchupRecord?.scoresByUser[homeUser.id] : undefined
                    const awayResult = awayUser ? matchupRecord?.scoresByUser[awayUser.id] : undefined
                    const gameId = matchupRecord?.gameId
                    const isClickable = typeof gameId === 'number'

                    return (
                      <tr
                        key={`${matchup.group}-${matchup.home}-${matchup.away}`}
                        className={isClickable ? 'group-play-matchup-row clickable' : 'group-play-matchup-row'}
                        onClick={isClickable ? () => navigate(`/games/${gameId}`) : undefined}
                        onKeyDown={
                          isClickable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  navigate(`/games/${gameId}`)
                                }
                              }
                            : undefined
                        }
                        role={isClickable ? 'link' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                      >
                        <td className="group-play-player-cell">
                          <div className="group-play-player-stack">
                            <div className="group-play-player-name">
                              {homeUser?.displayName ?? matchup.home}
                            </div>
                            {homeResult ? (
                              matchupRecord?.isForfeit ? (
                                <ForfeitMarker result={homeResult.forfeitResult} />
                              ) : (
                                <div className="group-play-score-row group-play-score-row-home">
                                  <TeamHelmet
                                    abbreviation={homeResult.teamAbbreviation}
                                    size="sm"
                                    className="group-play-score-helmet"
                                  />
                                  <span className="group-play-score-value">{homeResult.score}</span>
                                </div>
                              )
                            ) : null}
                          </div>
                        </td>
                        <td className="group-play-vs-cell">vs.</td>
                        <td className="group-play-player-cell group-play-player-cell-away">
                          <div className="group-play-player-stack group-play-player-stack-away">
                            <div className="group-play-player-name">
                              {awayUser?.displayName ?? matchup.away}
                            </div>
                            {awayResult ? (
                              matchupRecord?.isForfeit ? (
                                <ForfeitMarker result={awayResult.forfeitResult} align="away" />
                              ) : (
                                <div className="group-play-score-row group-play-score-row-away">
                                  <span className="group-play-score-value">{awayResult.score}</span>
                                  <TeamHelmet
                                    abbreviation={awayResult.teamAbbreviation}
                                    size="sm"
                                    className="group-play-score-helmet"
                                  />
                                </div>
                              )
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

function ForfeitMarker({
  result,
  align = 'home',
}: {
  result?: 'win' | 'loss'
  align?: 'home' | 'away'
}) {
  const isWin = result === 'win'
  return (
    <div className={`group-play-forfeit-marker group-play-forfeit-marker-${align}`}>
      <span className={isWin ? 'group-play-forfeit-win' : 'group-play-forfeit-loss'}>
        {isWin ? '✓' : '×'}
      </span>
      <span className="group-play-forfeit-label">{isWin ? 'Forfeit Win' : 'Forfeit Loss'}</span>
    </div>
  )
}
